import React, { useState, useEffect } from 'react';
import {
  Users,
  Building2,
  FileText,
  AlertTriangle,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Card, Loading } from '../../components/common';
import useAuthStore from '../../store/authStore';
import { organizacionesAPI, denunciasAPI } from '../../api';

const DashboardPage = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [resumenDenuncias, setResumenDenuncias] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Si es admin de org, cargar estadísticas de su organización
      if (user?.id_organizacion) {
        const [statsRes, denunciasRes] = await Promise.all([
          organizacionesAPI.getEstadisticas(user.id_organizacion),
          denunciasAPI.getResumen(user.id_organizacion),
        ]);

        setStats(statsRes.data);
        setResumenDenuncias(denunciasRes.data);
      }
    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, trend }) => (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
          {trend && (
            <p className="mt-2 flex items-center text-sm text-green-600">
              <TrendingUp className="h-4 w-4 mr-1" />
              {trend}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return <Loading fullScreen text="Cargando dashboard..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Bienvenido, {user?.nombre_completo}
        </h1>
        <p className="mt-2 text-gray-600">
          Aquí está el resumen de tu organización
        </p>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Usuarios"
            value={stats.total_usuarios || 0}
            icon={Users}
            color="bg-blue-500"
          />
          <StatCard
            title="Evaluaciones"
            value={stats.total_evaluaciones || 0}
            icon={CheckCircle}
            color="bg-green-500"
          />
          <StatCard
            title="Códigos de Ética"
            value={stats.total_codigos || 0}
            icon={FileText}
            color="bg-purple-500"
          />
          <StatCard
            title="Denuncias Activas"
            value={resumenDenuncias?.en_investigacion || 0}
            icon={AlertTriangle}
            color="bg-red-500"
          />
        </div>
      )}

      {/* Alertas y Resumen de Denuncias */}
      {resumenDenuncias && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Denuncias Críticas */}
          <Card
            title="Denuncias que Requieren Atención"
            className="border-l-4 border-red-500"
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Críticas sin resolver
                    </p>
                    <p className="text-xs text-gray-600">
                      Alta prioridad
                    </p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-red-600">
                  {resumenDenuncias.criticas_sin_resolver}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-yellow-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Próximas a vencer
                    </p>
                    <p className="text-xs text-gray-600">
                      Más de 30 días
                    </p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-yellow-600">
                  {resumenDenuncias.proximas_vencer}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Pendientes de asignación
                    </p>
                    <p className="text-xs text-gray-600">
                      Sin investigador
                    </p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-blue-600">
                  {resumenDenuncias.pendientes_asignacion}
                </span>
              </div>
            </div>
          </Card>

          {/* Acciones Rápidas */}
          <Card title="Acciones Rápidas">
            <div className="space-y-3">
              <button className="w-full flex items-center justify-between p-4 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors duration-200">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-primary-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    Nueva Evaluación
                  </span>
                </div>
                <span className="text-primary-600">→</span>
              </button>

              <button className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    Generar Código de Ética
                  </span>
                </div>
                <span className="text-green-600">→</span>
              </button>

              <button className="w-full flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    Ver Denuncias
                  </span>
                </div>
                <span className="text-red-600">→</span>
              </button>

              <button className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-purple-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    Gestionar Comité
                  </span>
                </div>
                <span className="text-purple-600">→</span>
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Información adicional */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card title="Actividad Reciente">
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>No hay actividad reciente</p>
          </div>
        </Card>

        <Card title="Próximas Tareas">
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>No hay tareas pendientes</p>
          </div>
        </Card>

        <Card title="Notificaciones">
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>No hay notificaciones</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
